import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useOrder, type OrderLineItem } from '../hooks/useOrder';
import { useOrders } from '../hooks/useOrders';
import { useDeleteOrder } from '../hooks/useOrderMutations';
import { LineItemTasks } from '../components/LineItemTasks';
import { OrderEditForm } from '../components/OrderEditForm';
import { ArrowLeft, Package, AlertCircle, Edit2, Trash2, Loader2 } from 'lucide-react';
import type { SalesOrderStatus } from 'scheduler-types';

const statusColors: Record<SalesOrderStatus, string> = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-blue-100 text-blue-700',
  complete: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: SalesOrderStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${statusColors[status]}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function DeleteConfirmModal({
  orderNumber,
  onConfirm,
  onCancel,
  isDeleting,
}: {
  orderNumber: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting: boolean;
}) {
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md border border-border mx-4 p-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">Delete Order</h3>
        <p className="text-sm text-foreground-muted mb-6">
          Are you sure you want to delete order <span className="font-mono font-medium text-foreground">{orderNumber}</span>? This will cancel the order and all associated tasks.
        </p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function LineItemCard({ item }: { item: OrderLineItem }) {
  const schedulingStatusColors: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    scheduled: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-800',
    complete: 'bg-green-100 text-green-700',
    review_required: 'bg-orange-100 text-orange-700',
  };

  return (
    <div className="bg-background rounded-lg border border-border p-4 hover:border-primary/50 transition-colors">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-background-muted flex items-center justify-center">
            <Package className="h-5 w-5 text-foreground-muted" />
          </div>
          <div>
            <p className="font-medium text-foreground">{item.partNumber}</p>
            <p className="text-sm text-foreground-muted">Qty: {item.quantity}</p>
          </div>
        </div>
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
            schedulingStatusColors[item.schedulingStatus] || 'bg-gray-100 text-gray-700'
          }`}
        >
          {item.schedulingStatus.replace('_', ' ')}
        </span>
      </div>
      <LineItemTasks lineItemId={item.id} />
    </div>
  );
}

function OrderDetailSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-6 bg-background-muted rounded w-32" />
      <div className="bg-background rounded-lg border border-border p-6">
        <div className="h-8 bg-background-muted rounded w-48 mb-2" />
        <div className="h-5 bg-background-muted rounded w-36" />
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="h-12 bg-background-muted rounded" />
          <div className="h-12 bg-background-muted rounded" />
          <div className="h-12 bg-background-muted rounded" />
        </div>
      </div>
      <div className="h-6 bg-background-muted rounded w-40" />
      <div className="space-y-4">
        <div className="h-20 bg-background-muted rounded" />
        <div className="h-20 bg-background-muted rounded" />
      </div>
    </div>
  );
}

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { data: order, isLoading, error } = useOrder(orderId);
  const { data: ordersData } = useOrders({ limit: 200 });
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteMutation = useDeleteOrder(orderId ?? '');

  // Compute priority rank from the sorted orders list
  const priorityRank = ordersData?.data
    ? ordersData.data.findIndex((o) => o.id === orderId) + 1
    : null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Link
          to="/orders"
          className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Orders
        </Link>
        <OrderDetailSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link
          to="/orders"
          className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Orders
        </Link>
        <div className="rounded-lg border border-danger bg-danger-subtle p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-danger" />
            <div>
              <h3 className="font-medium text-danger-foreground">Failed to load order</h3>
              <p className="text-sm text-danger-foreground mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <Link
          to="/orders"
          className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Orders
        </Link>
        <div className="rounded-lg border border-border bg-background p-8 text-center">
          <h3 className="text-lg font-medium text-foreground">Order not found</h3>
          <p className="text-sm text-foreground-muted mt-1">
            The order you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/orders"
        className="inline-flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Orders
      </Link>

      {/* Order header card */}
      <div className="bg-background rounded-lg border border-border p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {order.gpSalesOrderNumber}
            </h1>
            <p className="text-foreground-muted mt-1">{order.customerName}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={order.status} />
            {!isEditing && (
              <>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground-muted hover:text-danger border border-border rounded-md hover:bg-background-muted transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </button>
              </>
            )}
          </div>
        </div>

        {isEditing && (
          <OrderEditForm
            order={{
              id: order.id,
              customerName: order.customerName,
              promisedShipDate: order.promisedShipDate,
              status: order.status,
            }}
            onClose={() => setIsEditing(false)}
          />
        )}

        <div className="grid grid-cols-3 gap-6 mt-6 pt-6 border-t border-border">
          <div>
            <p className="text-sm text-foreground-muted">Promised Ship Date</p>
            <p className="font-medium text-foreground mt-1">{order.promisedShipDate ? format(parseISO(order.promisedShipDate), 'MMM d, yyyy') : '-'}</p>
          </div>
          <div>
            <p className="text-sm text-foreground-muted">Expected Ship Date</p>
            <p className="font-medium text-foreground mt-1">
              {order.expectedShipDate ? format(parseISO(order.expectedShipDate), 'MMM d, yyyy') : 'Not scheduled'}
            </p>
          </div>
          <div>
            <p className="text-sm text-foreground-muted">Priority</p>
            <p className="font-medium text-foreground mt-1">
              {priorityRank ? `#${priorityRank}` : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Line items section */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Line Items ({order.lineItems.length})
        </h2>
        {order.lineItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-background p-8 text-center">
            <Package className="h-8 w-8 text-foreground-muted mx-auto mb-3" />
            <p className="text-foreground-muted">No line items for this order.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {order.lineItems.map((item) => (
              <LineItemCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          orderNumber={order.gpSalesOrderNumber}
          onConfirm={() => {
            deleteMutation.mutate(undefined, {
              onSuccess: () => navigate('/orders'),
            });
          }}
          onCancel={() => setShowDeleteConfirm(false)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
